import { Box, Grid } from '@mui/material';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getWorkAssignmentsByRecruiterJobMappingId,
  ITaskResponse,
  ITaskSearchParam,
} from 'wisestep-api';
import {
  formattedDateISO,
  IFilterList,
  ScrollableTabs,
  TabTextVariant,
  WsDialog,
  WsFilterTags,
  WsListingLoader,
  WsPagination,
  WsTaskCard,
  WsTitleBanner,
} from 'wisestep-base';

import TasksAdvanceFilterFormProps from '../../components/filters/models/TasksAdvanceFiltersFormProps';
import NoDataFoundCard from '../../components/noDataFoundCard';
import { WsTaskCreationCard } from '../../components/WsTaskCreationCard';
import formatLocations from '../../utils/formatLocations';
import getEncodedQueryParams from '../../utils/getEncodedQueryParams';
import { useNotificationStore } from '../../utils/NotificationStore';
import { openUrlInNewTab } from '../../utils/openUrlInNewTab';
import { ClintPageBreadCrumb } from '../clientDetails/helper/clientDetailsHelper';

import TaskSideNav from './components/jobListSideNav';
import TaskSearch from './components/search';
import { TaskSearchRequestProps } from './components/search/TaskSearch';
// eslint-disable-next-line import/namespace
import useTaskSearch from './hooks/useTaskSearch';
import useTaskStats from './hooks/useTaskStats';
import { TaskDefaultQuery } from './TaskDefaultQuery';
import { defaultTaskFilterValues } from './utils/DefaultTaskFilterValues';
import { pushTaskFilters } from './utils/PushTaskFilters';
import { closeTask, taskList } from './utils/TaskHelper';

const TaskListing = () => {
  const params = useParams();
  const [appliedFilters, setAppliedFilters] = useState<IFilterList[]>([]);
  const defaultQuery = { ...TaskDefaultQuery };
  if (params.candidateEmail)
    defaultQuery.taskAttributes = { candidateEmail: params.candidateEmail };
  const [advancedFilterDefaultValues, setAdvancedFilterFields] =
    useState<TasksAdvanceFilterFormProps>(defaultTaskFilterValues);

  const [loader, setLoader] = useState<boolean>(false);
  const [query, setQuery] = useState<ITaskSearchParam>(defaultQuery);
  const [tasks, taskCount] = useTaskSearch(query, setLoader);
  const [selectedTaskIndex, setSelectedTaskIndex] = useState<number>(-1);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const navigate = useNavigate();

  const taskStats = useTaskStats(query);

  const changeStatus = (index: number, value: string) => {
    const updatedQuery = { ...query };
    updatedQuery.status = value.toUpperCase();
    setQuery(updatedQuery);
  };
  const pageChange = (event, value) => {
    const updatedQuery = { ...query };
    updatedQuery.page = value;
    setQuery(updatedQuery);
  };
  const selectedIndex = (index: number) => {
    const updatedQuery = { ...query };
    updatedQuery.searchType = index === 0 ? 'own' : 'team';
    setQuery(updatedQuery);
  };
  const search = (data: TaskSearchRequestProps) => {
    const updatedQuery = { ...query };
    updatedQuery.keywords = data.query ?? '';
    const arr = data.sort.split(',');
    updatedQuery.sort = arr[0];
    updatedQuery.sortDirection = arr[1];
    updatedQuery.dueFromDateTime = formattedDateISO(Date().toString());
    updatedQuery.dueTillDateTime = data.timePeriod ?? data.customTime;
    setQuery(updatedQuery);
  };
  const updateAdvancedFilterQuery = (data: TasksAdvanceFilterFormProps) => {
    const updatedQuery = { ...query };
    updatedQuery.team = [];
    data.teams?.forEach((obj) => updatedQuery.team?.push(obj.id ?? ''));
    updatedQuery.taskAttributes = {
      company: data.company ?? [],
      jobCode: data.jobCodes ?? [],
      jobTitle: data.jobTitles ?? [],
      jobLocation: formatLocations(data.locations) ?? [],
    };
    updatedQuery.createdFromDateTime = data.createBWDates?.fromDateName
      ? formattedDateISO(data.createBWDates?.fromDateName)
      : '';
    updatedQuery.createdTillDateTime = data.createBWDates?.toDateName
      ? formattedDateISO(data.createBWDates?.toDateName)
      : '';
    updatedQuery.updatedFromDateTime = data.updatedBWDates?.fromDateName
      ? formattedDateISO(data.updatedBWDates?.fromDateName)
      : '';
    updatedQuery.updatedTillDateTime = data.updatedBWDates?.toDateName
      ? formattedDateISO(data.updatedBWDates?.toDateName)
      : '';
    updatedQuery.createdBy = data.recruiters ?? [];

    return updatedQuery;
  };
  const onApplyFilters = (data: TasksAdvanceFilterFormProps) => {
    setQuery(updateAdvancedFilterQuery(data));
    setAdvancedFilterFields(data);
    setAppliedFilters(pushTaskFilters(data));
  };
  const onDeleteFilterTag = (tag: string) => {
    setAppliedFilters((prev) => {
      const idOfChip = prev.find((existChip) => existChip.tag === tag)?.id ?? '';
      const advancedFilterDefaults: TasksAdvanceFilterFormProps = advancedFilterDefaultValues;
      advancedFilterDefaults[idOfChip] = defaultTaskFilterValues[idOfChip];
      setAdvancedFilterFields((prev) => ({ ...prev, ...advancedFilterDefaults }));
      setQuery(updateAdvancedFilterQuery(advancedFilterDefaults));
      const index = prev.findIndex((existChip) => existChip.id === idOfChip);
      prev.splice(index, 1);
      return [...prev];
    });
  };
  const openJobCRM = async (id: string, candidateEmail: string) => {
    const response = await getWorkAssignmentsByRecruiterJobMappingId(id);

    if (response.data) {
      openUrlInNewTab(`/jobs-crm/${response.data.jobId}/false/0/pipeline/${candidateEmail}`);
    }
  };

  const onCompleteTask = async (taskId: string) => {
    setNotification(await closeTask(taskId));
    setQuery({ ...query });
  };

  const onNameCandidateClick = async (task: ITaskResponse) => {
    if (task.entityId !== null) {
      navigate(`/candidate-layout/profile?id=${task.entityId}&array=${[task.entityId]}`);
    }
  };

  const handleClientClick = (notePath, task) => {
    switch (notePath) {
      case 'CLIENT':
        navigate(
          `/client-details?tabIndex=0&isProfile=true&isChild=${
            task.parentId != null ? false : true
          }&name=${task.clientName}&clientId=${task.clientId}`
        );
        break;
      case 'CONTACT':
        navigate(
          `/${task.contactId}/client-contact-details?isChild=true&isProfile=true&name=${
            task.clientName
          }&breadCrumbList=${getEncodedQueryParams(ClintPageBreadCrumb['CLIENTS'])}`
        );
        break;
      default:
        navigate(
          `/client-details?tabIndex=0&isProfile=true&isChild=false&name=${
            task.clientName
          }&clientId=${task.clientId}&breadCrumbList=${getEncodedQueryParams(
            ClintPageBreadCrumb['CLIENTS']
          )}`
        );
    }
  };

  return (
    <Box>
      <Grid item xs={12} pb={2}>
        <WsTitleBanner title="Reminders" goBack={() => navigate('/home')} />
      </Grid>
      <ScrollableTabs
        scrollList={taskList}
        getTabIndex={selectedIndex}
        variant={TabTextVariant.SUB_TITLE}
        defaultValue={1}
      />
      <Grid item xs={12} container columnGap={2}>
        <Grid item xs={2} mt={2}>
          <TaskSideNav selectedIndexValue={changeStatus} taskStats={taskStats} />
        </Grid>
        <Grid item xs={9.8} justifyContent="flex-end">
          <TaskSearch
            onSearch={search}
            onApplyFilters={onApplyFilters}
            clearAdvanceFilters={() => {}}
            advancedFilterDefaultValues={advancedFilterDefaultValues}
          />
          <WsFilterTags filtersList={appliedFilters} onDelete={onDeleteFilterTag} />
          {tasks.length > 0 && !loader ? (
            <Box>
              {taskCount > 0 && (
                <Grid item xs={12} container justifyContent="flex-end" mt={2}>
                  <WsPagination
                    count={Math.ceil(taskCount / query?.size)}
                    onChange={pageChange}
                    itemsPerPage={query?.size}
                    pageNumber={query.page}
                    isPageCount={true}
                    type="Tasks"
                    totalCount={taskCount}
                  />
                </Grid>
              )}
              <Box>
                {tasks?.map((task, index) => (
                  <Box my={2} key={index}>
                    <WsTaskCard
                      checkBoxNeeded={false}
                      title={task.title ?? ''}
                      description={
                        task.attributes &&
                        `${task.recruiterName ?? ''} has a task about ${
                          task.attributes.jobTitle ?? ''
                        } with ${task.attributes.candidateName ?? ''}`
                      }
                      date={task.dueDate ? task.dueDate.toString() : task?.modifiedOn.toString()}
                      jobRole={
                        task.attributes && task.attributes?.jobTitle
                          ? `${task.attributes?.jobTitle},${task.attributes.jobCode}`
                          : ''
                      }
                      createdBy={task?.history[0]?.recruiterName ?? ''}
                      createdByImgUrl="http://jbdjkch.jdbcjkad/jbc/"
                      activeUsers={[]}
                      onSnoozeClick={() => {
                        setSelectedTaskIndex(index);
                        setOpenDialog(!openDialog);
                      }}
                      onEditClick={() => {
                        setSelectedTaskIndex(index);
                        setOpenDialog(!openDialog);
                      }}
                      onJobTitleClick={() =>
                        openJobCRM(
                          task?.attributes?.recruiterJobMappingId ?? '',
                          task.attributes?.candidateEmail ?? ''
                        )
                      }
                      onCandidateNameClick={() => onNameCandidateClick(task)}
                      taskStatus={task.status}
                      onCompleteTask={() => onCompleteTask(task.id)}
                      taskDescription={task.description ?? ''}
                      taskType={task.entityType}
                      jobId={(task.attributes && task.attributes.jobId) ?? ''}
                      clientName={task.clientName}
                      contactName={task.contactName}
                      onClientClick={(path) => handleClientClick(path, task)}
                    />
                  </Box>
                ))}
              </Box>
              <Box>
                <Grid item xs={12} container justifyContent="flex-end" mt={2}>
                  {taskCount > 0 && (
                    <WsPagination
                      count={Math.ceil(taskCount / query?.size)}
                      onChange={pageChange}
                      itemsPerPage={query?.size}
                      pageNumber={query.page}
                      isPageCount={true}
                      type="Tasks"
                      totalCount={taskCount}
                    />
                  )}
                </Grid>
              </Box>
            </Box>
          ) : loader ? (
            <WsListingLoader />
          ) : (
            <Box py={2}>
              <NoDataFoundCard text="No Tasks Found" />
            </Box>
          )}
        </Grid>
      </Grid>
      <WsDialog size="medium" openDialog={openDialog}>
        <WsTaskCreationCard
          updateTaskProp={true}
          taskDetails={tasks[selectedTaskIndex]}
          closeDialog={() => setOpenDialog(!openDialog)}
        />
      </WsDialog>
    </Box>
  );
};
export default TaskListing;
